import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface StockItem {
  id: string;
  name: string;
  sku: string;
  category: string;
  currentStock: number;
  minimumStock: number;
  maximumStock: number;
  unitPrice: number;
  location: string;
}

export const StockManagement: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);

  // Mock data - replace with API call
  const stockItems: StockItem[] = [
    {
      id: '1',
      name: 'Brake Pad Set',
      sku: 'BRK-001',
      category: 'Brakes',
      currentStock: 45,
      minimumStock: 20,
      maximumStock: 100,
      unitPrice: 1499.99,
      location: 'Aisle A1, Sandton Warehouse',
    },
    {
      id: '2',
      name: 'Oil Filter',
      sku: 'FLT-001',
      category: 'Filters',
      currentStock: 15,
      minimumStock: 30,
      maximumStock: 150,
      unitPrice: 249.99,
      location: 'Aisle B2, Midrand Store',
    },
    {
      id: '3',
      name: 'Spark Plug Set',
      sku: 'SPK-001',
      category: 'Ignition',
      currentStock: 60,
      minimumStock: 25,
      maximumStock: 120,
      unitPrice: 599.99,
      location: 'Aisle C3, Centurion Branch',
    },
    {
      id: '4',
      name: 'Willard Battery 643',
      sku: 'BAT-001',
      category: 'Batteries',
      currentStock: 8,
      minimumStock: 10,
      maximumStock: 50,
      unitPrice: 1899.99,
      location: 'Aisle D1, Sandton Warehouse',
    },
  ];

  const handleEditClick = (item: StockItem) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedItem(null);
    setOpenDialog(false);
  };

  const getStockLevel = (current: number, min: number, max: number) => {
    const percentage = (current / max) * 100;
    let color = 'success.main';
    if (current <= min) {
      color = 'error.main';
    } else if (current <= min * 1.5) {
      color = 'warning.main';
    }
    return { percentage, color };
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Stock Management
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Items</Typography>
                <Typography variant="h3">2,456</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Low Stock Items</Typography>
                <Typography variant="h3" color="warning.main">15</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Out of Stock</Typography>
                <Typography variant="h3" color="error.main">3</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Value</Typography>
                <Typography variant="h3" color="primary.main">R 2,45M</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Add New Item
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
            >
              Refresh Stock
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Stock Level</TableCell>
                  <TableCell>Unit Price</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stockItems.map((item) => {
                  const { percentage, color } = getStockLevel(
                    item.currentStock,
                    item.minimumStock,
                    item.maximumStock
                  );
                  return (
                    <TableRow key={item.id}>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                color={color === 'warning.main' ? 'warning' : color === 'error.main' ? 'error' : 'success'}
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography variant="body2" color="text.secondary">
                                {item.currentStock}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>R {item.unitPrice.toFixed(2)}</TableCell>
                      <TableCell>{item.location}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(item)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedItem ? 'Edit Stock Item' : 'Add New Stock Item'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  defaultValue={selectedItem?.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SKU"
                  defaultValue={selectedItem?.sku}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Category"
                  defaultValue={selectedItem?.category}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  defaultValue={selectedItem?.location}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Current Stock"
                  defaultValue={selectedItem?.currentStock}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Unit Price"
                  defaultValue={selectedItem?.unitPrice}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Stock"
                  defaultValue={selectedItem?.minimumStock}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Stock"
                  defaultValue={selectedItem?.maximumStock}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleCloseDialog}>
              {selectedItem ? 'Save Changes' : 'Add Item'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 